import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
  Loader2,
  Calendar,
  Clock,
  MapPin,
  User,
  Users,
  Check,
  Trash2,
} from 'lucide-react';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { Modal } from '../ui';

interface CompromissoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  eventoId?: number | null;
  dataInicial?: Date | null;
  tipos: TipoCompromisso[];
}

interface TipoCompromisso {
  id: number;
  codigo: string;
  nome: string;
  cor: string;
  icone: string | null;
}

interface CompromissoForm {
  titulo: string;
  descricao: string;
  tipo: string;
  dataInicio: string;
  horaInicio: string;
  dataFim: string;
  horaFim: string;
  diaInteiro: boolean;
  local: string;
  lembreteMinutos: number | null;
  cor: string;
  usuarioId: number | null;
  leadId: number | null;
  veiculoId: number | null;
  pessoaId: number | null;
  status: string;
}

interface Usuario {
  id: number;
  nome: string;
}

interface Lead {
  id: number;
  nome: string;
}

export function CompromissoModal({ isOpen, onClose, onSave, eventoId, dataInicial, tipos }: CompromissoModalProps) {
  const { showSuccess, showError } = useNotification();

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<CompromissoForm>({
    defaultValues: {
      titulo: '',
      descricao: '',
      tipo: '',
      dataInicio: '',
      horaInicio: '09:00',
      dataFim: '',
      horaFim: '10:00',
      diaInteiro: false,
      local: '',
      lembreteMinutos: 30,
      cor: '#3B82F6',
      usuarioId: null,
      leadId: null,
      veiculoId: null,
      pessoaId: null,
      status: 'PENDENTE',
    },
  });

  const diaInteiro = watch('diaInteiro');
  const tipoSelecionado = watch('tipo');

  // Atualizar cor quando tipo mudar
  useEffect(() => {
    if (tipoSelecionado) {
      const tipo = tipos.find(t => t.codigo === tipoSelecionado);
      if (tipo) {
        setValue('cor', tipo.cor);
      }
    }
  }, [tipoSelecionado, tipos, setValue]);

  const fetchCompromisso = useCallback(async () => {
    if (!eventoId) return;

    setIsLoading(true);
    try {
      const response = await api.get(`/agenda/${eventoId}`);
      const data = response.data;

      const dataInicio = new Date(data.dataInicio);
      const dataFim = data.dataFim ? new Date(data.dataFim) : null;

      reset({
        titulo: data.titulo || '',
        descricao: data.descricao || '',
        tipo: data.tipo || '',
        dataInicio: dataInicio.toISOString().split('T')[0],
        horaInicio: dataInicio.toTimeString().slice(0, 5),
        dataFim: dataFim ? dataFim.toISOString().split('T')[0] : dataInicio.toISOString().split('T')[0],
        horaFim: dataFim ? dataFim.toTimeString().slice(0, 5) : '10:00',
        diaInteiro: data.diaInteiro || false,
        local: data.local || '',
        lembreteMinutos: data.lembreteMinutos,
        cor: data.cor || '#3B82F6',
        usuarioId: data.usuarioId,
        leadId: data.leadId,
        veiculoId: data.veiculoId,
        pessoaId: data.pessoaId,
        status: data.status || 'PENDENTE',
      });
    } catch {
      showError('Erro ao carregar compromisso');
    } finally {
      setIsLoading(false);
    }
  }, [eventoId, reset, showError]);

  const fetchUsuarios = useCallback(async () => {
    try {
      // A API retorna um PagedResult, então precisamos extrair os items
      const response = await api.get('/usuarios');
      const data = response.data;
      // Verifica se é um array ou um objeto paginado
      if (Array.isArray(data)) {
        setUsuarios(data);
      } else if (data?.items) {
        setUsuarios(data.items);
      } else {
        setUsuarios([]);
      }
    } catch {
      // Silenciar
      setUsuarios([]);
    }
  }, []);

  const fetchLeads = useCallback(async () => {
    try {
      const response = await api.get('/leads?status=ABERTO&pageSize=100');
      setLeads(response.data.items || []);
    } catch {
      // Silenciar
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchUsuarios();
      fetchLeads();

      if (eventoId) {
        fetchCompromisso();
      } else if (dataInicial) {
        const dataStr = dataInicial.toISOString().split('T')[0];
        const horaStr = dataInicial.toTimeString().slice(0, 5);
        reset({
          titulo: '',
          descricao: '',
          tipo: '',
          dataInicio: dataStr,
          horaInicio: horaStr || '09:00',
          dataFim: dataStr,
          horaFim: horaStr ? addHour(horaStr) : '10:00',
          diaInteiro: false,
          local: '',
          lembreteMinutos: 30,
          cor: '#3B82F6',
          usuarioId: null,
          leadId: null,
          veiculoId: null,
          pessoaId: null,
          status: 'PENDENTE',
        });
      }
    }
  }, [isOpen, eventoId, dataInicial, fetchCompromisso, fetchUsuarios, fetchLeads, reset]);

  const addHour = (time: string): string => {
    const [hours, minutes] = time.split(':').map(Number);
    const newHours = (hours + 1) % 24;
    return `${newHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const onSubmit = async (data: CompromissoForm) => {
    setIsSaving(true);
    try {
      const dataInicio = new Date(`${data.dataInicio}T${data.diaInteiro ? '00:00' : data.horaInicio}`);
      const dataFim = data.dataFim
        ? new Date(`${data.dataFim}T${data.diaInteiro ? '23:59' : data.horaFim}`)
        : null;

      const payload = {
        titulo: data.titulo,
        descricao: data.descricao || null,
        tipo: data.tipo || null,
        dataInicio: dataInicio.toISOString(),
        dataFim: dataFim?.toISOString() || null,
        diaInteiro: data.diaInteiro,
        local: data.local || null,
        lembreteMinutos: data.lembreteMinutos,
        cor: data.cor,
        usuarioId: data.usuarioId || null,
        leadId: data.leadId || null,
        veiculoId: data.veiculoId || null,
        pessoaId: data.pessoaId || null,
        status: data.status,
      };

      if (eventoId) {
        await api.put(`/agenda/${eventoId}`, payload);
        showSuccess('Compromisso atualizado com sucesso');
      } else {
        await api.post('/agenda', payload);
        showSuccess('Compromisso criado com sucesso');
      }

      onSave();
    } catch {
      showError('Erro ao salvar compromisso');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!eventoId || !confirm('Deseja realmente excluir este compromisso?')) return;

    setIsSaving(true);
    try {
      await api.delete(`/agenda/${eventoId}`);
      showSuccess('Compromisso excluido com sucesso');
      onSave();
    } catch {
      showError('Erro ao excluir compromisso');
    } finally {
      setIsSaving(false);
    }
  };

  const handleConcluir = async () => {
    if (!eventoId) return;

    setIsSaving(true);
    try {
      await api.post(`/agenda/${eventoId}/concluir`);
      showSuccess('Compromisso concluido');
      onSave();
    } catch {
      showError('Erro ao concluir compromisso');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={eventoId ? 'Editar Compromisso' : 'Novo Compromisso'} size="lg">
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Titulo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Titulo *
            </label>
            <input
              type="text"
              {...register('titulo', { required: 'Titulo obrigatorio' })}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.titulo ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Ex: Reuniao com cliente"
            />
            {errors.titulo && (
              <p className="text-red-500 text-sm mt-1">{errors.titulo.message}</p>
            )}
          </div>

          {/* Tipo e Cor */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo
              </label>
              <select
                {...register('tipo')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Selecione...</option>
                {tipos.map((tipo) => (
                  <option key={tipo.codigo} value={tipo.codigo}>
                    {tipo.nome}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cor
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  {...register('cor')}
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  {...register('cor')}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="#3B82F6"
                />
              </div>
            </div>
          </div>

          {/* Dia Inteiro */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="diaInteiro"
              {...register('diaInteiro')}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="diaInteiro" className="text-sm text-gray-700">
              Dia inteiro
            </label>
          </div>

          {/* Data e Hora */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Data Inicio *
              </label>
              <input
                type="date"
                {...register('dataInicio', { required: 'Data obrigatoria' })}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.dataInicio ? 'border-red-500' : 'border-gray-300'
                }`}
              />
            </div>
            {!diaInteiro && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Hora Inicio
                </label>
                <input
                  type="time"
                  {...register('horaInicio')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Data Fim
              </label>
              <input
                type="date"
                {...register('dataFim')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {!diaInteiro && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Hora Fim
                </label>
                <input
                  type="time"
                  {...register('horaFim')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}
          </div>

          {/* Local */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <MapPin className="w-4 h-4 inline mr-1" />
              Local
            </label>
            <input
              type="text"
              {...register('local')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: Escritorio, Casa do cliente..."
            />
          </div>

          {/* Descricao */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descricao
            </label>
            <textarea
              {...register('descricao')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Detalhes do compromisso..."
            />
          </div>

          {/* Relacionamentos */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <User className="w-4 h-4 inline mr-1" />
                Responsavel
              </label>
              <select
                {...register('usuarioId', { valueAsNumber: true })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Selecione...</option>
                {usuarios.map((u) => (
                  <option key={u.id} value={u.id}>{u.nome}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Users className="w-4 h-4 inline mr-1" />
                Lead
              </label>
              <select
                {...register('leadId', { valueAsNumber: true })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Selecione...</option>
                {leads.map((l) => (
                  <option key={l.id} value={l.id}>{l.nome}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Lembrete */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lembrete
            </label>
            <select
              {...register('lembreteMinutos', { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Sem lembrete</option>
              <option value={15}>15 minutos antes</option>
              <option value={30}>30 minutos antes</option>
              <option value={60}>1 hora antes</option>
              <option value={120}>2 horas antes</option>
              <option value={1440}>1 dia antes</option>
            </select>
          </div>

          {/* Status (apenas edicao) */}
          {eventoId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                {...register('status')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="PENDENTE">Pendente</option>
                <option value="CONFIRMADO">Confirmado</option>
                <option value="CONCLUIDO">Concluido</option>
                <option value="CANCELADO">Cancelado</option>
              </select>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div>
              {eventoId && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={isSaving}
                    className="flex items-center gap-1 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Excluir
                  </button>
                  <button
                    type="button"
                    onClick={handleConcluir}
                    disabled={isSaving}
                    className="flex items-center gap-1 px-3 py-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                  >
                    <Check className="w-4 h-4" />
                    Concluir
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                {eventoId ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </div>
        </form>
      )}
    </Modal>
  );
}
